// Phase 60: cross-account transfer helpers.
//
// A transfer = 2 linked tx rows sharing transfer_group_id:
//   - Row 1: type='expense', account_local_uuid=from, amount=N
//   - Row 2: type='income',  account_local_uuid=to,   amount=N
//
// Both rows MUST be created in the same logical operation so balances
// stay consistent. The Transactions page can filter by transfer_group_id
// or by tags='transfer' to show "only transfers".

const NOTE_PREFIX = 'Transfer';

/**
 * Validate a transfer request.
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export function validateTransfer({ fromAccountId, toAccountId, amount, accounts }) {
  if (!fromAccountId) return { ok: false, reason: 'missing_from' };
  if (!toAccountId) return { ok: false, reason: 'missing_to' };
  if (fromAccountId === toAccountId) return { ok: false, reason: 'same_account' };
  if (!amount || amount <= 0) return { ok: false, reason: 'invalid_amount' };
  // Phase 68: when accounts is provided (even empty), verify ids exist.
  // When accounts is undefined/null, skip the lookup (caller is responsible).
  if (accounts !== undefined && accounts !== null) {
    if (!accounts.find(a => a.local_uuid === fromAccountId)) return { ok: false, reason: 'from_not_found' };
    if (!accounts.find(a => a.local_uuid === toAccountId)) return { ok: false, reason: 'to_not_found' };
  }
  return { ok: true };
}

/**
 * Build 2 linked tx rows (in-memory only; caller persists via writes.addTransaction).
 * @returns {{ txFrom, txTo, transfer_group_id }}
 */
export function buildTransfer({ fromAccountId, toAccountId, amount, note, occurredOn }) {
  const ts = new Date().toISOString();
  const gid = (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
  const transferNote = note || `${NOTE_PREFIX}`;
  const today = occurredOn || ts.slice(0, 10);
  const baseRow = {
    server_id: null,
    amount,
    occurred_on: today,
    category_id: null,
    category_name: 'Transfer',
    category_icon: '🔄',
    category_color: '#64748b',
    note: transferNote,
    tags: 'transfer',
    transfer_group_id: gid,
    credit_card_local_uuid: null,
    credit_card_id: null,
    deleted: false,
    created_at: ts,
    updated_at: ts,
  };
  return {
    transfer_group_id: gid,
    txFrom: { ...baseRow, local_uuid: cryptoUuid(), type: 'expense', account_local_uuid: fromAccountId },
    txTo:   { ...baseRow, local_uuid: cryptoUuid(), type: 'income',  account_local_uuid: toAccountId },
  };
}

function cryptoUuid() {
  return (typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = Math.random() * 16 | 0;
        return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
      });
}

/**
 * Format a transfer label like "現金 → 銀行"
 */
export function transferLabel(fromName, toName) {
  return `${fromName} → ${toName}`;
}
